
import React from 'react';
import { SiteTheme, StyleMode } from '../../../types';

export const THEME_PRESETS: { id: string; name: string; colors: Partial<SiteTheme> }[] = [
    {
        id: 'classic',
        name: 'Classic Roast',
        colors: {
            primary: '#1A120B',
            secondary: '#816358',
            accent: '#D8C8C0',
            background: '#FDFBF9',
            surface: '#FFFFFF',
        }
    },
    {
        id: 'nordic',
        name: 'Nordic Light',
        colors: {
            primary: '#292524', // Stone 800
            secondary: '#57534e', // Stone 600
            accent: '#e7e5e4', // Stone 200
            background: '#ffffff',
            surface: '#fafaf9', // Stone 50
        }
    },
    {
        id: 'espresso',
        name: 'Dark Espresso',
        colors: {
            primary: '#ffffff',
            secondary: '#a8a29e', // Stone 400
            accent: '#44403c', // Stone 700
            background: '#0c0a09', // Stone 950
            surface: '#1c1917', // Stone 900
        }
    },
    {
        id: 'berry',
        name: 'Berry Brew',
        colors: {
            primary: '#4a044e', // Fuchsia 950
            secondary: '#701a75', // Fuchsia 800
            accent: '#f0abfc', // Fuchsia 300
            background: '#f9f2f9',
            surface: '#ffffff',
        }
    },
    {
        id: 'forest',
        name: 'Organic Forest',
        colors: {
            primary: '#064e3b', // Emerald 900
            secondary: '#047857', // Emerald 700
            accent: '#6ee7b7', // Emerald 300
            background: '#f2f9f8',
            surface: '#ffffff',
        }
    }
];

// STYLE DEFAULTS (Used if no granular control is set)
const MODE_DEFAULTS: Record<StyleMode, { radius: number, glass: number, fontH: string, fontB: string, scale: 'sm' | 'md' | 'lg' }> = {
    modern: { radius: 16, glass: 20, fontH: 'var(--font-app)', fontB: 'var(--font-app)', scale: 'md' },
    classic: { radius: 4, glass: 5, fontH: 'var(--font-app)', fontB: 'var(--font-app)', scale: 'md' },
    soft: { radius: 24, glass: 30, fontH: 'var(--font-app)', fontB: 'var(--font-app)', scale: 'sm' }, // Soft defaults to small
    bold: { radius: 0, glass: 0, fontH: 'var(--font-app)', fontB: 'var(--font-app)', scale: 'lg' }, // Bold defaults to large
};

// Helper: Convert Hex to RGB object
const hexToRgb = (hex: string) => {
    // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
    const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    hex = hex.replace(shorthandRegex, function(m, r, g, b) {
        return r + r + g + g + b + b;
    });

    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
    } : { r: 255, g: 255, b: 255 }; // Default to white if invalid
};

/**
 * Generates the CSS variables object based on the theme configuration.
 * Handles backward compatibility and granular overrides.
 */
export const generateThemeStyles = (theme: SiteTheme): React.CSSProperties => {
    const mode = theme.styleMode || 'modern';
    const defaults = MODE_DEFAULTS[mode];

    // Use specific value if set, otherwise fall back to mode default
    const radius = theme.componentRadius !== undefined ? theme.componentRadius : defaults.radius;
    const glass = theme.glassDensity !== undefined ? theme.glassDensity : defaults.glass;

    // Radius Calculations
    const radiusBtn = radius === 0 ? '0px' : radius > 30 ? '99px' : `${radius - 4}px`;
    const radiusCard = `${radius}px`;

    // Handle "Bold" mode edge case (Hard borders)
    const isBold = mode === 'bold';

    // Glass Calculations (0 = none, 40 = heavy)
    const isGlassDisabled = glass === 0;

    const glassBgOpacity = isGlassDisabled ? 0 : Math.min(0.95, 0.05 + (glass / 100 * 2)); 
    const glassBlur = isGlassDisabled ? '0px' : `${Math.max(4, glass)}px`;
    // const glassBorderOpacity = isGlassDisabled ? 0 : Math.min(0.5, 0.1 + (glass / 100)); // REMOVED

    // Determine Base Tint Color
    // 1. Custom Tint (if set)
    // 2. Bold Mode = Primary Color
    // 3. Default = White
    const tintColorHex = theme.glassTintColor || (isBold ? theme.primary : '#FFFFFF');
    const tintRgb = hexToRgb(tintColorHex);
    const glassBgString = `rgba(${tintRgb.r}, ${tintRgb.g}, ${tintRgb.b}, ${glassBgOpacity})`;
    
    // For border: Bold mode always keeps its hard border. 
    // Standard modes now imply TRANSPARENT glass border.
    const glassBorderString = isBold 
        ? '4px solid var(--color-accent)' 
        : 'transparent';

    
    // Determine Text Colors for Hero/Boxes based on mode
    // Classic/Soft usually have white glass boxes -> Need Dark Text
    // Modern/Bold usually have transparent or solid dark boxes -> Need White/Light Text
    const isLightBox = mode === 'classic' || mode === 'soft';
    const heroTitleColor = isLightBox ? 'var(--color-primary)' : (mode === 'bold' ? 'var(--color-surface)' : '#ffffff');
    const heroTextColor = isLightBox ? 'var(--color-secondary)' : (mode === 'bold' ? 'var(--color-surface)' : 'rgba(255,255,255,0.9)');

    // Construct Variables
    return {
        '--font-heading': defaults.fontH,
        '--font-body': defaults.fontB,
        
        '--radius-btn': radiusBtn,
        '--radius-card': radiusCard,
        
        '--glass-bg': glassBgString,
        '--glass-border': glassBorderString,
        '--glass-blur': isBold ? '0px' : glassBlur,
        
        '--hero-align': mode === 'bold' ? 'flex-start' : 'center',
        '--hero-text': mode === 'bold' ? 'left' : 'center',
        
        // Dynamic Text Colors for Hero Sections
        '--hero-color-title': heroTitleColor,
        '--hero-color-text': heroTextColor,

        // PRODUCT CARD SPECIFICS (New)
        '--card-border': theme.cardBorderColor || 'var(--color-accent)', // Fallback to accent
        '--card-bg': theme.cardBackgroundColor || '#FFFFFF',
        '--card-title': theme.cardTitleColor || 'var(--color-primary)',
        '--card-price': theme.cardPriceColor || 'var(--color-secondary)',
        '--card-btn-bg': theme.cardButtonColor || 'var(--color-surface)',
        '--card-btn-text': theme.cardButtonTextColor || 'var(--color-primary)',

    } as React.CSSProperties;
};

// Legacy Export for safety
export const STYLE_DEFINITIONS = {
    modern: generateThemeStyles({ primary:'', secondary:'', accent:'', background:'', surface:'', styleMode: 'modern' }),
    classic: generateThemeStyles({ primary:'', secondary:'', accent:'', background:'', surface:'', styleMode: 'classic' }),
    soft: generateThemeStyles({ primary:'', secondary:'', accent:'', background:'', surface:'', styleMode: 'soft' }),
    bold: generateThemeStyles({ primary:'', secondary:'', accent:'', background:'', surface:'', styleMode: 'bold' }),
};
